import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import CookieConsent from '../components/CookieConsent';
import SEOHead from '../components/SEOHead';
import { getImagePath } from '../utils/imagePath';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const email = (formData.email || '').trim();
    const password = (formData.password || '').trim();
    const result = await login(email, password);
    
    if (result.success) {
      // Rediriger vers la page d'accueil authentifiée (Home) après connexion
      navigate('/home');
    } else {
      setError(result.error || t('login.error'));
    }
    
    setLoading(false);
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-navy-500 to-navy-700">
      <SEOHead 
        title={t('login.title')}
        description={t('login.subtitle')}
        keywords="connexion, authentification, Golden Mali, accès plateforme"
        noindex={true}
      />
      <div className="bg-white p-8 rounded-lg shadow-2xl w-full max-w-md border border-navy-200">
        {/* Logo et titre */}
        <div className="text-center mb-8">
          <img 
            src={getImagePath("/assets/logo/GM.png")} 
            alt="Golden Mali SARL" 
            className="h-20 w-20 mx-auto mb-4 rounded-lg bg-white p-2 shadow-lg"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <h1 className="text-3xl font-bold text-navy-800 mb-2">{t('login.title')}</h1>
          <p className="text-sm text-navy-600">{t('login.subtitle')}</p>
        </div>

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div 
              id="error-message"
              role="alert"
              aria-live="assertive"
              className="bg-red-100 border border-red-300 text-red-700 px-4 py-3 rounded-lg text-sm"
            >
              {error}
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-navy-700 mb-2">
              {t('login.email')}
            </label>
            <input
              id="email"
              type="text"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              autoComplete="username"
              aria-required="true"
              aria-invalid={error ? 'true' : 'false'}
              aria-describedby={error ? 'error-message' : undefined}
              className="w-full px-4 py-3 border-2 border-navy-200 rounded-lg focus:border-gold-500 focus:outline-none focus:ring-2 focus:ring-gold-500 focus:ring-offset-2 text-navy-800 transition-all"
              placeholder="votre@email.com"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-semibold text-navy-700 mb-2">
              {t('login.password')}
            </label>
            <input
              id="password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              autoComplete="current-password"
              aria-required="true"
              aria-invalid={error ? 'true' : 'false'}
              aria-describedby={error ? 'error-message' : undefined}
              className="w-full px-4 py-3 border-2 border-navy-200 rounded-lg focus:border-gold-500 focus:outline-none focus:ring-2 focus:ring-gold-500 focus:ring-offset-2 text-navy-800 transition-all"
              placeholder="••••••••"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            aria-busy={loading}
            aria-label={loading ? t('login.submitting') : t('login.submit')}
            className="w-full btn-gold py-4 text-lg font-bold disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-gold-500 focus:ring-offset-2"
          >
            {loading ? t('login.submitting') : t('login.submit')}
          </button>
        </form>


        <div className="mt-6 text-center text-sm text-navy-500">
          <p>{t('login.version')}</p>
        </div>
      </div>

      {/* Popup d'acceptation des cookies - Affiché systématiquement */}
      <CookieConsent />
    </div>
  );
};

export default Login;
